import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { Quote, X } from "lucide-react";
import { MILESTONES } from "@/data/content";
import { Reveal, SectionHead } from "@/components/Reveal";

function MilestoneModal({ m, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-8"
      data-testid="timeline-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="timeline-modal-backdrop" />
      <motion.div
        initial={{ y: 48, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 24, opacity: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 240 }}
        className="relative z-10 max-h-[86vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-line/25 bg-ink shadow-2xl"
      >
        <div className="relative">
          <img src={m.img} alt={m.title} className="h-52 w-full rounded-t-3xl object-cover sm:h-60" />
          <div className="absolute inset-0 rounded-t-3xl bg-gradient-to-t from-ink via-transparent to-transparent" />
          <span className="glass absolute left-5 top-5 rounded-full px-4 py-1.5 font-mono text-[11px] tracking-[0.3em] text-gold">
            {m.label}
          </span>
          <button
            onClick={onClose}
            data-testid="timeline-modal-close"
            aria-label="Close"
            className="glass absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full text-fg transition-colors hover:text-gold"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 sm:p-8">
          <h3 className="font-display text-2xl font-bold text-fg sm:text-3xl" data-testid="timeline-modal-title">{m.title}</h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-soft/60" data-testid="timeline-modal-caption">{m.caption}</p>
          {m.body.map((p, i) => (
            <p key={i} className="mt-4 leading-relaxed text-soft">{p}</p>
          ))}
          <div className="mt-6 rounded-2xl border-l-2 border-gold bg-panel/60 p-5">
            <div className="flex items-center gap-2 text-gold">
              <Quote className="h-4 w-4" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Legacy Note</span>
            </div>
            <p className="mt-2 font-serif2 text-lg italic leading-relaxed text-fg/90" data-testid="timeline-modal-note">{m.note}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Timeline() {
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.65"] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="timeline" ref={ref} className="relative overflow-x-clip py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHead
          eyebrow="1935 — 2026"
          title="Ninety-One Years, One Chandbagh"
          sub="Walk the milestones that built the school — tap any card to open its story."
        />
        <div className="relative">
          <div className="absolute bottom-0 left-[7px] top-0 w-px bg-line/15 md:left-1/2" />
          <motion.div
            style={{ scaleY: lineScale }}
            className="absolute bottom-0 left-[7px] top-0 w-px origin-top bg-gradient-to-b from-gold via-gold/70 to-gold/10 md:left-1/2"
            data-testid="timeline-progress-line"
          />
          <div className="space-y-10 md:space-y-16">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.id} delay={0.05} x={i % 2 ? 48 : -48} className={`relative pl-7 md:pl-0 md:w-[46%] ${i % 2 ? "md:ml-auto" : ""}`}>
                <button
                  onClick={() => setOpen(m)}
                  data-testid={`timeline-card-${m.id}`}
                  className="glass group w-full rounded-3xl p-6 text-left transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_20px_60px_rgba(0,0,0,0.35)]"
                >
                  <div className="absolute left-[2px] top-8 h-3 w-3 rounded-full bg-gold md:hidden" style={{ boxShadow: "0 0 16px rgba(212,175,55,0.7)" }} />
                  <div className={`absolute left-4 top-8 hidden h-3 w-3 rounded-full bg-gold md:block ${i % 2 ? "-left-[calc(50%+4.5rem)]" : "right-[calc(50%+4.5rem)] -translate-x-1/2 md:right-auto md:left-[-3.2rem]"}`} style={{ boxShadow: "0 0 16px rgba(212,175,55,0.7)" }} />
                  <div className="flex items-center justify-between gap-4">
                    <span className="rounded-full border border-gold/30 px-3 py-1 font-mono text-[11px] tracking-[0.25em] text-gold">{m.label}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-soft opacity-0 transition-opacity duration-300 group-hover:opacity-100">Read story →</span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold text-fg transition-colors duration-300 group-hover:text-gold sm:text-2xl">{m.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-soft">{m.excerpt}</p>
                  <div className="mt-5 h-28 w-full overflow-hidden rounded-2xl">
                    <motion.div
                      className="h-full w-full will-change-transform"
                      initial={{ scale: 1 }}
                      whileInView={{ scale: 1.09 }}
                      viewport={{ amount: 0.3 }}
                      transition={{ duration: 16, ease: "linear" }}
                      data-testid={`kenburns-${m.id}`}
                    >
                      <img src={m.img} alt={m.title} loading="lazy" decoding="async" className="h-full w-full object-cover sepia-[0.35] transition-all duration-700 group-hover:scale-105 group-hover:sepia-0" />
                    </motion.div>
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-soft/60" data-testid={`timeline-caption-${m.id}`}>{m.caption}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
      <AnimatePresence>{open && <MilestoneModal m={open} onClose={() => setOpen(null)} />}</AnimatePresence>
    </section>
  );
}
